import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import {
  buildControlledAccessHoldingHref,
  resolveAuthenticatedHoldHref,
} from './accessRedirect'
import {
  hasAppInteriorGrant,
  resolveHoldReason,
  shouldHoldAuthenticatedUser,
} from './accessPolicy'
import { isAppInteriorPath } from './controlledAccessPaths'
import { SessionLoadingShell } from './SessionLoadingShell'
import { useAuthSession } from './useAuthSession'

/**
 * Blocks app product interiors until session + grant are valid (B1/B5).
 * Preserves deep links via redirectTo on /access holding.
 */
export function AppInteriorFailClosedGate({ children }: { children: React.ReactNode }) {
  const { status, access, error } = useAuthSession()
  const location = useLocation()

  const pathname = location.pathname
  const interior = isAppInteriorPath(pathname)

  if (!interior) {
    return <>{children}</>
  }

  const redirectTo = `${pathname}${location.search}${location.hash}`

  if (status === 'loading') {
    return <SessionLoadingShell label="Verifying access…" />
  }

  if (status === 'anonymous') {
    const reason = resolveHoldReason('anonymous', access, error)
    return (
      <Navigate
        to={buildControlledAccessHoldingHref({ redirectTo, reason })}
        replace
      />
    )
  }

  if (shouldHoldAuthenticatedUser(access) || !hasAppInteriorGrant(access)) {
    return (
      <Navigate
        to={resolveAuthenticatedHoldHref({
          redirectTo,
          access,
          sessionError: error,
        })}
        replace
      />
    )
  }

  return <>{children}</>
}
